<template>
	<button
		:class="classes"
		@click="click"
		@click.middle="$emit('middle-click', $event)"
		:disabled="disabled"
		:tabindex="tabindex"
	>
		<div v-if="isLoading">
			<font-awesome-icon
				class="mr-2"
				:icon="['fas', 'spinner-third']"
				spin
			/>
		</div>
		<div
			class="flex flex-row items-center inside"
			:class="{'align-left': alignLeft}"
			v-else
		>
			<font-awesome-icon
				class="flex-none hdl-button-icon"
				:class="(label !== '' && label)? 'mr-2' : ''"
				:icon="icon"
				fixed-width
				v-if="icon"
			/>
			<slot><div class="flex-none">{{ label }}</div></slot>
			<div
				class="shortcut flex-none ml-2 flex flex-row items-center"
				v-if="shortcutComponents && shortcutComponents.length"
			>
				<div
					v-for="component of shortcutComponents"
					:key="component"
					class="shortcut-component"
				>
					{{ component }}
				</div>
			</div>
			<font-awesome-icon
				class="flex-none end-icon"
				:class="(label !== '' && label)? 'ml-2' : ''"
				:icon="endIcon"
				fixed-width
				v-if="endIcon"
			/>
		</div>
	</button>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-facing-decorator';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinnerThird } from '@fortawesome/pro-solid-svg-icons';

library.add( faSpinnerThird );

import { CSSClasses } from '@/common/types';

import { kbShortcutStringToComponents } from '@/common/utils';

export declare type HdlButtonStyle = 'primary' | 'default' | 'secondary' | 'action-on-gray' | 'transparent' | 'secondary-on-gray' | 'tertiary' | 'danger' | 'form' | 'plain' | 'toolbar' | 'action-menu' | 'question-choice-action' | 'dropdown';
export declare type HdlButtonSize = 'sm-x' | 'sm' | 'md' | 'lg';

@Component({
	emits: [
		'click', 'middle-click'
	]
})
export default class HdlButton extends Vue {
	@Prop({ default: '' })
	label: string;

	@Prop({ default: false, type: Boolean })
	isLoading: boolean;

	@Prop({ default: false, type: Boolean })
	disabled: boolean;

	@Prop({ default: 'default' })
	hdlStyle: HdlButtonStyle;

	@Prop({ default: 'md' })
	size: HdlButtonSize;

	@Prop({ default: false, type: Boolean })
	toggled: boolean;

	@Prop({ default: null })
	icon: [string, string] | null;

	@Prop({ default: null })
	endIcon: [string, string] | null;

	@Prop({ default: null })
	shortcut: string | null;

	@Prop({ default: false, type: Boolean })
	alignLeft: boolean;

	@Prop({ default: 0 })
	tabindex: number;

	shortcutComponents: Array<string> | null = null;

	click(event: MouseEvent): boolean {
		if (this.disabled) {
			return true;
		}

		this.$emit('click', event);
		return false;
	}

	get classes(): CSSClasses {
		const classes: CSSClasses = {
			'hdl-button': true
		};

		classes[this.hdlStyle] = true;
		classes[this.size] = true;

		classes['toggled'] = this.toggled;

		classes['icon-only'] = this.label === '';

		classes['disabled'] = this.disabled;

		return classes;
	}

	@Watch('shortcut', { immediate: true })
	watchShortcut(shortcut: string | null): void {
		if (shortcut === null || shortcut.trim().length === 0) {
			this.shortcutComponents = null;
			return;
		}

		this.shortcutComponents = kbShortcutStringToComponents(shortcut, true, true);
	}

}
</script>

<style lang="scss">
html[dark] {
	.hdl-button {
		&.tertiary {
			.shortcut {
				color: var(--text-secondary);
			}
		}
	}
}

.hdl-button {
	@apply rounded border border-transparent;
	box-shadow: rgba(0,0,0, .05) 0 2px 2px, rgba(0,0,0, .02) 0 -1px 2px;

	&:hover {
		box-shadow: rgba(0,0,0, .05) 0 2px 2px, rgba(0,0,0, .02) 0 -1px 2px;
	}

	.shortcut {
		font-size: 0.75rem;
		color: var(--text-secondary);
	}

	&:focus {
		@apply border-accent-border;
		outline: none;
	}

	&.sm {
		@apply py-2 px-2;
	}

	&.sm-x {
		@apply px-1;

		.end-icon {
			@apply relative;
			top: 2px;
		}
	}

	&.md {
		@apply py-2;
		padding-left: 1rem;
		padding-right: 1rem;

		.hdl-button-icon {
			font-size: 14px;
			margin-right: 4px;
		}
	}

	&.icon-only {
		box-shadow: none;
		&.sm,
		&.md,
		&.lg {
			.hdl-button-icon {
				margin-right: 0;
			}
		}

		&.lg,
		&.md {
			padding-left: .5rem;
			padding-right: .5rem;

			.hdl-button-icon {
				margin-right: 0;
			}
		}

		&.md {
			.hdl-button-icon {
				font-size: 14px;
				padding: 4px 0;
			}
		}

		&.lg {
			.hdl-button-icon {
				font-size: 18px;
				padding: 2px 0;
			}
		}
	}

	&.lg {
		@apply py-2 px-4 text-lg;
	}

	&.disabled {
		@apply cursor-default text-disabled;
	}

	&.default {
		@apply hover:bg-fill-hover;

		&.toggled {
			@apply bg-base-black text-white;
		}

		.shortcut {
			color: var(--text-secondary);
		}
	}

	&.question-choice-action {
		@apply bg-transparent hover:bg-fill-hover text-disabled text-sm;

		&.toggled {
			@apply text-main;
		}

		.shortcut {
			color: var(--text-secondary);
		}

		&:active,
		&:focus {
			@apply border-transparent;
		}
	}

	&.plain {
		@apply hover:bg-fill-hover;

		box-shadow: none;

		&:hover {
			box-shadow: none;
		}
	}

	&.toolbar {
		@apply hover:bg-fill-hover;

		box-shadow: none;

		&:hover {
			box-shadow: none;
		}

		&.sm {
			padding: 3px;
		}
	}

	&.action-menu {
		@apply hover:bg-fill-hover text-disabled;
		box-shadow: none;

		&:focus {
			@apply bg-fill-hover;
			border: 1px solid transparent;
		}

		&:hover {
			box-shadow: none;
		}
	}

	&.form {
		@apply border border-border-gray bg-fill;
	}

	&.transparent {
		@apply bg-transparent text-text-secondary hover:text-primary;
	}

	&.toggle {
		@apply bg-fill-gray hover:bg-fill-gray-hover text-disabled;

		&.toggled {
			@apply bg-base-black text-white;
		}

		box-shadow: none;

		&:hover {
			box-shadow: none;
		}

		&.sm:not(.icon-only) {
			min-width: 40px;
		}
	}

	&.action-on-gray {
		@apply bg-fill-gray-secondary w-7 hover:bg-fill-gray-hover;
	}

	&.tertiary,
	&.secondary-on-gray,
	&.danger,
	&.primary {
		@apply bg-fill-gray;
	}

	&.dropdown,
	&.secondary {
		@apply bg-fill hover:bg-fill-hover;
		border: 1px solid rgba(var(--mainRGB), .15);

		&:focus {
			@apply border-accent-border border;
			outline: none;
		}

		&:active {
			@apply border-transparent;
		}
	}

	&.secondary-on-gray {
		@apply bg-fill-gray-secondary border-transparent text-text-secondary hover:bg-fill-gray-secondary-hover;

		&:focus {
			@apply border-accent-border border;
			outline: none;
		}

		&:active {
			@apply border-transparent;
		}
	}

	&.tertiary {
		@apply bg-fill-gray-tertiary border-fill-gray-tertiary text-white;

		&.disabled {
			@apply cursor-default;
			background: var(--fill-gray-tertiary-disabled);
		}

		.shortcut {
			color: var(--dark-gray-bg);
		}

		&:focus {
			@apply border-accent-border border;
			outline: none;
		}

		&:active {
			@apply border-transparent;
		}
	}

	&.danger {
		@apply bg-error border-error-border text-white;

		&.disabled {
			@apply cursor-default border-transparent;
			background: var(--red-bg);
		}
	}

	&.primary {
		@apply bg-fill-accent rounded-md border-accent-border text-accent-primary;
		min-width: 40px;

		&.icon-only {
			height: 40px;
			padding-left: 0;
			padding-right: 0;

			.contents {
				@apply h-full;
			}
		}

		&.disabled {
			@apply cursor-default;
			background: rgba(var(--mainRGB), .8);
		}
	}

	.inside {
		@apply flex justify-center items-center;

		&.align-left {
			@apply justify-start;
		}
	}

	.shortcut-component {
		padding: .1rem;
		margin-right: .1rem;
	}
}
</style>
